import React, { useState, useContext, useRef, useEffect } from 'react'
import '../styles/NewsV1.css'
import { AuthContext } from '../context/AuthContext'
import useClickOutside from '../hooks/useClickOutside'
import useResponsiveWidth from '../hooks/useResponsiveWidth'
import { ThemeContext } from '../context/ThemeContext'
import {
  getImageUrl,
  formatCurrency as fc,
  customStylesTrans,
  LoadingSpinner,
  formatModifiedTicker,
} from '../helper/StylesHelper'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import config from '../config'

const NewsV1 = () => {
  useResponsiveWidth()
  const navigate = useNavigate()

  const { isAuthenticated, username, signOut, profilePicture, userToken } =
      useContext(AuthContext)
  const { tc } = useContext(ThemeContext)

  const [selectedTime, setSelectedTime] = useState('Today')
  const [selectedSort, setSelectedSort] = useState('Most popular')
  const [loading, setLoading] = useState(false)
  const [newsData, setNewsData] = useState([])
  const [trendingTopics, setTrendingTopics] = useState([])
  const [expandedStories, setExpandedStories] = useState({})

  const timeOptions = ['Today', 'This Week', 'This Month']

  // Helper function to truncate HTML content
  const truncateHtmlContent = (htmlContent, wordLimit = 30) => {
    if (!htmlContent) return ''
    
    // Create a temporary div to parse HTML
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = htmlContent
    
    // Get text content without HTML tags
    const textContent = tempDiv.textContent || tempDiv.innerText || ''
    
    // Split into words and truncate
    const words = textContent.split(' ')
    if (words.length <= wordLimit) return htmlContent
    
    const truncatedText = words.slice(0, wordLimit).join(' ')
    return truncatedText + '...'
  }

  // Helper function to check if content needs truncation
  const shouldTruncate = (htmlContent, wordLimit = 30) => {
    if (!htmlContent) return false
    
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = htmlContent
    const textContent = tempDiv.textContent || tempDiv.innerText || ''
    const words = textContent.split(' ')
    
    return words.length > wordLimit
  }

  // Toggle expand/collapse for a specific story
  const toggleStoryExpansion = (storyId, event) => {
    event.stopPropagation() // Prevent triggering the story click handler
    setExpandedStories(prev => ({
      ...prev,
      [storyId]: !prev[storyId]
    }))
  }

  // Simulate fetching news data
  const fetchNewsData = async () => {
    setLoading(true)
    try {
      // Simulate API delay
      const { data: newsData } = await axios.get(
        `${config.LBbackendUrl}/api/posts/get-news-tagged?size=${10}`,
        {
          headers: {
            Authorization: `Bearer ${userToken}`,
          },
        }
      );

      var result = [];
      if ((newsData.data) && (newsData.data).length > 0) {
        (newsData.data).map((item) => {
          result.push({
            id: item._id,
            title: item.title,
            excerpt: item.content,
            author: item.author.username,
            time: item.timePosted,
            image: item.images ? item.images[0] : '',
            category: 'Markets'
          })
        })
      }

      // In a real app, this would be an API call
      setNewsData(result)
      setTrendingTopics(mockTrendingTopics)
    } catch (error) {
      console.error('Error fetching news:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleStoryClick = (story) => {
    navigate(`/post/${story.id}`)
  }

  const handleTrendingClick = (topic) => {
    navigate(`/post/${topic.id}`)
    // This could filter news by topic or navigate to a topic page
  }

  const mockTrendingTopics = [
    'Major Indices Post Weekly Gains',
    'Tesla Shares Rebound',
    'Lululemon Hit With Tariff Pressure',
    'Bill Gates and Elon Musk Have a Secret Child They Used as a Test Vaccine Patient'
  ]

  useEffect(() => {
    fetchNewsData()
  }, [selectedTime, selectedSort])

  const getCurrentDate = () => {
    const today = new Date()
    const options = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }
    return today.toLocaleDateString('en-US', options)
  }

  const getGreeting = () => {
    const hour = new Date().getHours()
    const name = username ? username : 'Guest' // This could come from user context

    if (hour < 12) return `Good Morning, ${name}`
    if (hour < 18) return `Good Afternoon, ${name}`
    return `Good Evening, ${name}`
  }

  // Helper function to format time to "August 11, 2025 13:45 hours"
  const formatNewsTime = (timeString) => {
    if (!timeString) return ''
    
    const date = new Date(timeString)
    
    // Check if date is valid
    if (isNaN(date.getTime())) return timeString
    
    const options = {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }
    
    const dateString = date.toLocaleDateString('en-US', options)
    const hours = date.getHours().toString().padStart(2, '0')
    const minutes = date.getMinutes().toString().padStart(2, '0')
    
    return `${dateString} ${hours}:${minutes}`
  }

  const renderMainNews = () => {
    if (loading) {
      return <LoadingSpinner />
    }

    if (newsData.length === 0) {
      return (
        <div className="news-empty">
          <p>No news articles available at the moment.</p>
        </div>
      )
    }

    const mainStory = newsData[0]

    return (
      <div className="news-main-section">
        {mainStory && (
          <div className="news-hero-story" onClick={() => handleStoryClick(mainStory)}>
            {mainStory.image && (
              <div className="news-hero-image-container">
                <img
                  src={mainStory.image}
                  alt={mainStory.title}
                  className="news-hero-image"
                  onError={(e) => {
                    e.target.style.display = 'none'
                  }}
                />
              </div>
            )}
            <div className="news-hero-content">
              <h1 className="news-hero-title">{mainStory.title}</h1>
              <div className="news-hero-excerpt-container">
                <div 
                  className="news-hero-excerpt"
                  dangerouslySetInnerHTML={{ 
                    __html: expandedStories[mainStory.id] 
                      ? mainStory.excerpt 
                      : truncateHtmlContent(mainStory.excerpt, 50) 
                  }} 
                />
                {shouldTruncate(mainStory.excerpt, 50) && (
                  <button 
                    className="news-expand-button"
                    onClick={(e) => toggleStoryExpansion(mainStory.id, e)}
                  >
                    {expandedStories[mainStory.id] ? 'Show less' : 'Read more'}
                  </button>
                )}
              </div>
              <div className="news-hero-meta">
                <span className="news-source">{mainStory.author}</span>
                <span className="news-dot">•</span>
                <span className="news-time">{formatNewsTime(mainStory.time)}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  const renderSidebarNews = () => {
    const sideStories = newsData.slice(1)

    return (
      <div className="news-sidebar-stories">
        {sideStories.map((story) => (
          <div
            key={story.id}
            className="news-sidebar-story"
            onClick={() => handleStoryClick(story)}
          >
            <h3 className="news-sidebar-title">{story.title}</h3>
            <div className="news-sidebar-excerpt-container">
              <div 
                className="news-sidebar-excerpt"
                dangerouslySetInnerHTML={{ 
                  __html: expandedStories[story.id] 
                    ? story.excerpt 
                    : truncateHtmlContent(story.excerpt, 25) 
                }} 
              />
              {shouldTruncate(story.excerpt, 25) && (
                <button 
                  className="news-expand-button news-expand-button-small"
                  onClick={(e) => toggleStoryExpansion(story.id, e)}
                >
                  {expandedStories[story.id] ? 'Show less' : 'Read more'}
                </button>
              )}
            </div>
            <div className="news-sidebar-meta">
              <span className="news-source">{story.author}</span>
              <span className="news-dot">•</span>
              <span className="news-time">{formatNewsTime(story.time)}</span>
            </div>
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className={`news-v1-container ${tc()}`}>


      <div className="news-v1-content">
        {/* Left Sidebar */}
        <div className="news-left-sidebar">
          <div className="news-date-section">
            <p className="news-date">{getCurrentDate()}</p>
            <h1 className="news-greeting">{getGreeting()}</h1>
          </div>

          <div className="news-time-filters">
            {timeOptions.map((time) => (
              <button
                key={time}
                className={`news-time-button ${selectedTime === time ? 'active' : ''}`}
                onClick={() => setSelectedTime(time)}
              >
                {time}
              </button>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div className="news-main-content">
          {renderMainNews()}
        </div>

        {/* Right Sidebar */}
        <div className="news-right-sidebar">
          {renderSidebarNews()}
        </div>
      </div>
    </div>
  )
}

export default NewsV1
